import { useState, useEffect, useRef, useCallback } from 'react'
import { MessageCircle, Send, Loader2, Bot, ChevronLeft, Minimize2 } from 'lucide-react'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../context/AuthContext'

const BOT_ID = '__bot__'

const fmtTime = iso => {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
}

const Avatar = ({ profile, size = 8 }) => {
  const name = profile?.nickname || profile?.full_name || '?'
  const letter = name[0]?.toUpperCase() ?? '?'
  const url = profile?.avatar_url ? `${profile.avatar_url}?t=0` : null
  return (
    <div className={`w-${size} h-${size} rounded-full bg-violet-600 flex items-center justify-center text-white text-xs font-bold overflow-hidden flex-shrink-0`}>
      {url ? <img src={url} alt={name} className="w-full h-full object-cover" /> : letter}
    </div>
  )
}

const BotAvatar = () => (
  <div className="w-8 h-8 rounded-full bg-indigo-600/30 border border-indigo-500/50 flex items-center justify-center flex-shrink-0">
    <Bot size={14} className="text-indigo-300" />
  </div>
)

const FloatingChat = () => {
  const { profile: myProfile } = useAuth()
  const [open, setOpen] = useState(false)
  const [profiles, setProfiles] = useState([])
  const [conversations, setConversations] = useState([])
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [messages, setMessages] = useState([])
  const [testo, setTesto] = useState('')
  const [sending, setSending] = useState(false)
  const [botTyping, setBotTyping] = useState(false)
  const [botUnread, setBotUnread] = useState(0)
  const [botLastMsg, setBotLastMsg] = useState(null)
  const bottomRef = useRef(null)
  const channelRef = useRef(null)
  const selectedUserIdRef = useRef(null)
  const inputRef = useRef(null)
  const myId = myProfile?.id

  useEffect(() => { selectedUserIdRef.current = selectedUserId }, [selectedUserId])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, botTyping])

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus()
  }, [open, selectedUserId])

  /* ── fetch profili — solo colleghi di progetto + conversazioni esistenti ── */
  useEffect(() => {
    if (!myId) return
    const load = async () => {
      const { data: all } = await supabase.from('profiles')
        .select('id, full_name, nickname, avatar_url')
        .neq('id', myId)
        .order('full_name', { nullsFirst: false })
      const tutti = all || []

      if (myProfile?.role === 'admin') { setProfiles(tutti); return }

      const { data: myMemb } = await supabase
        .from('project_members').select('project_id').eq('user_id', myId)
      const pids = (myMemb || []).map(r => r.project_id)

      const visibleIds = new Set()
      if (pids.length > 0) {
        const { data: membri } = await supabase
          .from('project_members').select('user_id').in('project_id', pids)
        ;(membri || []).forEach(r => { if (r.user_id !== myId) visibleIds.add(r.user_id) })
      }

      const { data: msgs } = await supabase.from('chat_messaggi')
        .select('sender_id, receiver_id')
        .or(`sender_id.eq.${myId},receiver_id.eq.${myId}`)
        .not('sender_id', 'is', null)
      ;(msgs || []).forEach(m => {
        const partner = m.sender_id === myId ? m.receiver_id : m.sender_id
        if (partner) visibleIds.add(partner)
      })

      setProfiles(tutti.filter(p => visibleIds.has(p.id)))
    }
    load()
  }, [myId, myProfile?.role])

  /* ── fetch bot meta ── */
  const fetchBotMeta = useCallback(async () => {
    if (!myId) return
    try {
      const [{ data: last }, { count }] = await Promise.all([
        supabase.from('chat_messaggi')
          .select('*').is('sender_id', null).eq('receiver_id', myId)
          .order('created_at', { ascending: false }).limit(1),
        supabase.from('chat_messaggi')
          .select('id', { count: 'exact', head: true })
          .is('sender_id', null).eq('receiver_id', myId).is('letto_at', null),
      ])
      setBotLastMsg(last?.[0] || null)
      setBotUnread(count || 0)
    } catch {}
  }, [myId])

  /* ── build conversazioni ── */
  const buildConversations = useCallback(async () => {
    if (!myId || profiles.length === 0) return
    try {
      const { data: msgs } = await supabase
        .from('chat_messaggi')
        .select('id, sender_id, receiver_id, contenuto, created_at, letto_at, is_deleted, hidden_for_sender, hidden_for_receiver')
        .or(`sender_id.eq.${myId},receiver_id.eq.${myId}`)
        .not('sender_id', 'is', null)
        .order('created_at', { ascending: false })
      if (!msgs) return
      const map = {}
      msgs.forEach(m => {
        if (m.hidden_for_sender && m.sender_id === myId) return
        if (m.hidden_for_receiver && m.receiver_id === myId) return
        const partner = m.sender_id === myId ? m.receiver_id : m.sender_id
        if (!map[partner]) map[partner] = { lastMessage: m, unread: 0 }
        if (m.receiver_id === myId && !m.letto_at && !m.is_deleted) map[partner].unread++
      })
      profiles.forEach(p => { if (!map[p.id]) map[p.id] = { lastMessage: null, unread: 0 } })
      const list = profiles.map(p => ({
        other: p,
        lastMessage: map[p.id]?.lastMessage ?? null,
        unread: map[p.id]?.unread ?? 0,
      }))
      list.sort((a, b) => {
        if (a.lastMessage && b.lastMessage)
          return new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at)
        if (a.lastMessage) return -1
        if (b.lastMessage) return 1
        return (a.other?.nickname || a.other?.full_name || '').localeCompare(b.other?.nickname || b.other?.full_name || '')
      })
      setConversations(list)
      fetchBotMeta()
    } catch {}
  }, [myId, profiles, fetchBotMeta])

  useEffect(() => { buildConversations() }, [buildConversations])

  /* ── fetch messaggi ── */
  const fetchMessages = useCallback(async () => {
    if (!myId || !selectedUserId) return
    try {
      if (selectedUserId === BOT_ID) {
        const { data } = await supabase.from('chat_messaggi').select('*')
          .or(`and(sender_id.eq.${myId},receiver_id.is.null),and(sender_id.is.null,receiver_id.eq.${myId})`)
          .order('created_at', { ascending: true })
        setMessages(data || [])
        await supabase.from('chat_messaggi')
          .update({ letto_at: new Date().toISOString() })
          .is('sender_id', null).eq('receiver_id', myId).is('letto_at', null)
        setBotUnread(0)
        if (!data || data.length === 0) {
          setBotTyping(true)
          supabase.functions.invoke('chat-bot', { body: { message: '__intro__' } })
            .then(({ data: res, error }) => { if (res?.skipped || error) setBotTyping(false) })
            .catch(() => setBotTyping(false))
        }
        return
      }
      const { data } = await supabase.from('chat_messaggi').select('*')
        .or(`and(sender_id.eq.${myId},receiver_id.eq.${selectedUserId}),and(sender_id.eq.${selectedUserId},receiver_id.eq.${myId})`)
        .order('created_at', { ascending: true })
      setMessages(data || [])
      await supabase.from('chat_messaggi')
        .update({ letto_at: new Date().toISOString() })
        .eq('sender_id', selectedUserId).eq('receiver_id', myId).is('letto_at', null)
      buildConversations()
    } catch {}
  }, [myId, selectedUserId, buildConversations])

  useEffect(() => { fetchMessages() }, [fetchMessages])

  /* ── realtime ── */
  useEffect(() => {
    if (!myId) return
    if (channelRef.current) supabase.removeChannel(channelRef.current)
    const ch = supabase.channel(`floating-chat-${myId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messaggi', filter: `receiver_id=eq.${myId}` },
        payload => {
          const msg = payload.new
          const isBotMsg = msg.sender_id === null
          const matchesSel = isBotMsg ? selectedUserIdRef.current === BOT_ID : msg.sender_id === selectedUserIdRef.current
          if (matchesSel) {
            setMessages(prev => prev.find(m => m.id === msg.id) ? prev : [...prev, msg])
            supabase.from('chat_messaggi').update({ letto_at: new Date().toISOString() }).eq('id', msg.id)
            if (isBotMsg) { setBotUnread(0); setBotTyping(false) }
          } else if (isBotMsg) {
            setBotUnread(prev => prev + 1); setBotLastMsg(msg)
          }
          buildConversations()
        })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'chat_messaggi', filter: `receiver_id=eq.${myId}` },
        () => { buildConversations(); fetchBotMeta() })
      .subscribe()
    channelRef.current = ch
    return () => { supabase.removeChannel(ch) }
  }, [myId]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ── send ── */
  const handleSend = async () => {
    if (!testo.trim() || !selectedUserId) return

    if (selectedUserId === BOT_ID) {
      if (botTyping) return
      const msgText = testo.trim(); setTesto('')
      const tempId = `temp-${Date.now()}`
      setMessages(prev => [...prev, { id: tempId, sender_id: myId, receiver_id: null, contenuto: msgText, created_at: new Date().toISOString() }])
      const { data: saved } = await supabase.from('chat_messaggi')
        .insert({ sender_id: myId, receiver_id: null, contenuto: msgText }).select().single()
      if (saved) setMessages(prev => prev.map(m => m.id === tempId ? saved : m))
      setBotTyping(true)
      try {
        const { error: fnError } = await supabase.functions.invoke('chat-bot', { body: { message: msgText } })
        if (fnError) throw new Error(fnError.message)
      } catch (e) {
        setMessages(prev => [...prev, { id: `err-${Date.now()}`, sender_id: null, receiver_id: myId, contenuto: `⚠️ ${e.message}`, created_at: new Date().toISOString() }])
      } finally { setBotTyping(false) }
      return
    }

    setSending(true)
    const { data: newMsg } = await supabase.from('chat_messaggi')
      .insert({ sender_id: myId, receiver_id: selectedUserId, contenuto: testo.trim() }).select().single()
    if (newMsg) setMessages(prev => [...prev, newMsg])
    setTesto(''); setSending(false); buildConversations()
  }

  const handleKey = e => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  const isBotSelected = selectedUserId === BOT_ID
  const selectedProfile = profiles.find(p => p.id === selectedUserId)
  const visibleMessages = messages.filter(m => {
    if (m.hidden_for_sender && m.sender_id === myId) return false
    if (m.hidden_for_receiver && m.receiver_id === myId) return false
    return true
  })

  const totalUnread = conversations.reduce((acc, c) => acc + c.unread, 0) + botUnread

  return (
    <>
      {/* Pannello */}
      {open && (
        <div className="fixed bottom-[5.5rem] right-4 lg:bottom-6 z-50 w-[calc(100vw-2rem)] max-w-sm flex flex-col bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl overflow-hidden"
          style={{ height: '520px' }}>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 flex-shrink-0">
            {selectedUserId ? (
              <>
                <button onClick={() => { setSelectedUserId(null); setMessages([]) }}
                  className="text-gray-500 hover:text-white transition-colors p-1 -ml-1">
                  <ChevronLeft size={18} />
                </button>
                <div className="flex items-center gap-2 flex-1 min-w-0 mx-2">
                  {isBotSelected ? <BotAvatar /> : <Avatar profile={selectedProfile} />}
                  <span className="text-sm font-semibold text-white truncate">
                    {isBotSelected ? 'PlutonicBot' : (selectedProfile?.nickname || selectedProfile?.full_name)}
                  </span>
                </div>
              </>
            ) : (
              <p className="text-sm font-semibold text-white flex-1">Chat</p>
            )}
            <button onClick={() => setOpen(false)}
              className="text-gray-500 hover:text-white transition-colors p-1">
              <Minimize2 size={15} />
            </button>
          </div>

          {/* Lista conversazioni */}
          {!selectedUserId && (
            <div className="flex-1 overflow-y-auto">
              {/* Bot */}
              <button onClick={() => setSelectedUserId(BOT_ID)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-800/60 border-l-2 border-transparent transition-colors">
                <BotAvatar />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-300 truncate">PlutonicBot</span>
                    {botLastMsg && <span className="text-[10px] text-gray-600">{fmtTime(botLastMsg.created_at)}</span>}
                  </div>
                  <p className="text-xs text-gray-600 truncate mt-0.5">
                    {botLastMsg ? botLastMsg.contenuto?.split('\n')[0] : 'AI Assistant'}
                  </p>
                </div>
                {botUnread > 0 && (
                  <span className="text-[10px] bg-indigo-600 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center flex-shrink-0">{botUnread}</span>
                )}
              </button>
              <div className="mx-4 h-px bg-gray-800/60" />

              {conversations.map(c => {
                const nome = c.other?.nickname || c.other?.full_name || 'Utente'
                const lastText = c.lastMessage?.is_deleted ? '🚫 Messaggio eliminato' : c.lastMessage?.contenuto || ''
                return (
                  <button key={c.other.id} onClick={() => setSelectedUserId(c.other.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-800/60 border-l-2 border-transparent transition-colors">
                    <Avatar profile={c.other} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-sm font-medium truncate ${c.unread > 0 ? 'text-white' : 'text-gray-300'}`}>{nome}</span>
                        {c.lastMessage && <span className="text-[10px] text-gray-600">{fmtTime(c.lastMessage.created_at)}</span>}
                      </div>
                      {c.lastMessage && (
                        <p className={`text-xs truncate mt-0.5 ${c.unread > 0 ? 'text-gray-300' : 'text-gray-500'}`}>
                          {!c.lastMessage.is_deleted && c.lastMessage.sender_id === myId ? 'Tu: ' : ''}{lastText}
                        </p>
                      )}
                    </div>
                    {c.unread > 0 && (
                      <span className="text-[10px] bg-violet-600 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center flex-shrink-0">{c.unread}</span>
                    )}
                  </button>
                )
              })}
            </div>
          )}

          {/* Messaggi */}
          {selectedUserId && (
            <>
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
                {visibleMessages.length === 0 && !botTyping && (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-600 text-sm text-center">
                      {isBotSelected ? 'Ciao! Chiedimi qualcosa.' : 'Nessun messaggio. Di\' qualcosa! 👋'}
                    </p>
                  </div>
                )}
                {visibleMessages.map(m => {
                  const isMine = m.sender_id === myId
                  const isBot = m.sender_id === null
                  return (
                    <div key={m.id} className={`flex gap-2 items-end ${isMine ? 'flex-row-reverse' : 'flex-row'}`}>
                      {!isMine && (isBot
                        ? <BotAvatar />
                        : <Avatar profile={selectedProfile} size={6} />
                      )}
                      <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                        m.is_deleted ? 'bg-gray-800/50 border border-gray-700 text-gray-500 italic text-xs'
                        : isMine ? 'bg-violet-600 text-white rounded-br-sm'
                        : isBot ? 'bg-indigo-950/70 border border-indigo-500/20 text-indigo-100 rounded-bl-sm'
                        : 'bg-gray-800 text-gray-200 rounded-bl-sm'
                      }`}>
                        {m.is_deleted ? '🚫 Messaggio eliminato' : m.contenuto}
                        <div className={`text-[10px] mt-0.5 text-right ${isMine ? 'text-violet-200' : isBot ? 'text-indigo-400/50' : 'text-gray-600'}`}>
                          {fmtTime(m.created_at)}
                        </div>
                      </div>
                    </div>
                  )
                })}
                {isBotSelected && botTyping && (
                  <div className="flex gap-2 items-end">
                    <BotAvatar />
                    <div className="bg-indigo-950/70 border border-indigo-500/20 px-3 py-2.5 rounded-2xl rounded-bl-sm flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="px-3 py-3 border-t border-gray-800 flex-shrink-0">
                <div className={`flex items-end gap-2 bg-gray-800 rounded-xl px-3 py-2 border transition-colors focus-within:border-violet-500/50 ${isBotSelected ? 'border-indigo-700/40' : 'border-gray-700'}`}>
                  <textarea
                    ref={inputRef}
                    value={testo}
                    onChange={e => setTesto(e.target.value)}
                    onKeyDown={handleKey}
                    placeholder={isBotSelected ? 'Scrivi a PlutonicBot…' : 'Scrivi un messaggio…'}
                    rows={1}
                    className="flex-1 bg-transparent text-sm text-gray-200 placeholder-gray-600 resize-none outline-none max-h-24 leading-relaxed"
                  />
                  <button onClick={handleSend}
                    disabled={!testo.trim() || sending || (isBotSelected && botTyping)}
                    className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${isBotSelected ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-violet-600 hover:bg-violet-500'}`}>
                    {sending || (isBotSelected && botTyping)
                      ? <Loader2 size={13} className="text-white animate-spin" />
                      : <Send size={13} className="text-white" />
                    }
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* FAB */}
      {!open && (
        <button onClick={() => setOpen(true)}
          className="fixed bottom-20 right-4 lg:bottom-6 z-50 w-12 h-12 rounded-full bg-violet-600 hover:bg-violet-500 hover:scale-110 shadow-lg shadow-violet-900/40 flex items-center justify-center transition-all duration-200"
          title="Chat">
          <div className="relative">
            <MessageCircle size={20} className="text-white" />
            {totalUnread > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
                {totalUnread > 9 ? '9+' : totalUnread}
              </span>
            )}
          </div>
        </button>
      )}
    </>
  )
}

export default FloatingChat
